import { Component, ElementRef, input, output, viewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
	selector: 'ai-chat-input',
	template: `
		<div id="text-input-container" class="ai-chat-input-container" [ngStyle]="styles()?.container">
			<textarea
				#inputEl
				id="text-input"
				class="ai-chat-input-textarea"
				rows="1"
				[ngStyle]="styles()?.text"
				[placeholder]="placeholder()"
				[disabled]="disabled()"
				[(ngModel)]="value"
				(ngModelChange)="autoGrow()"
				(keydown.enter)="onEnter($event)"
				(focus)="focused = true"
				(blur)="focused = false"
			></textarea>
			<button
				type="button"
				class="ai-chat-input-submit"
				[class.has-text]="value.trim()"
				[disabled]="disabled() || !value.trim()"
				(click)="submitValue()"
			>
				<i class="far fa-paper-plane"></i>
			</button>
		</div>
		@if (characterLimit()) {
			<div class="ai-chat-input-help" translate="Forms.FieldErrorMessages.MaxCharacters" [translateParams]="{ count: characterLimit() }"></div>
		}
	`,
	styles: `
		:host {
			display: block;
			flex: 0 0 auto;
			padding: 8px 16px 12px;
		}
		.ai-chat-input-container {
			position: relative;
			display: flex;
			align-items: flex-end;
			gap: 8px;
			background: #fff;
			border: 1px solid #d9dde3;
			border-radius: 5px;
			box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
			padding: 8px 8px 8px 16px;
			transition: border-color .15s, box-shadow .15s;
		}
		.ai-chat-input-container:focus-within {
			border-color: #1558A6;
			box-shadow: 0 0 0 3px rgba(21, 88, 166, 0.12);
		}
		.ai-chat-input-textarea {
			flex: 1 1 auto;
			resize: none;
			border: none;
			outline: none;
			background: transparent;
			max-height: 160px;
			line-height: 1.4;
			padding: 6px 0;
		}
		.ai-chat-input-textarea::placeholder {
			color: #9aa2ae;
		}
		.ai-chat-input-submit {
			flex: 0 0 auto;
			width: 32px;
			height: 32px;
			border-radius: 50%;
			border: none;
			display: flex;
			align-items: center;
			justify-content: center;
			background: #e5e8ec;
			color: #9aa2ae;
			cursor: pointer;
			transition: background-color .15s, color .15s;
		}
		.ai-chat-input-submit.has-text {
			background: #1558A6;
			color: #fff;
		}
		.ai-chat-input-submit:disabled {
			cursor: default;
		}
		.ai-chat-input-help {
			margin-top: 4px;
			padding-left: 16px;
			opacity: 0.5;
			font-size: 0.75em;
		}
	`,
	imports: [CommonModule, FormsModule, TranslateModule],
})
export class AIChatInputComponent {
	placeholder = input<string>('')
	characterLimit = input<number | undefined>(undefined)
	disabled = input(false)
	styles = input<{ text?: Record<string, string>, container?: Record<string, string>, focus?: Record<string, string> }>()

	submitted = output<string>()

	value = ''
	focused = false

	inputEl = viewChild<ElementRef<HTMLTextAreaElement>>('inputEl')

	onEnter(event: Event) {
		const kbEvent = event as KeyboardEvent
		if (kbEvent.shiftKey) return;
		kbEvent.preventDefault();
		this.submitValue();
	}

	submitValue() {
		const text = this.value.trim();
		if (!text || this.disabled()) return;
		this.submitted.emit(text);
		this.value = '';
		this.autoGrow();
	}

	autoGrow() {
		const el = this.inputEl()?.nativeElement
		if (!el) return;
		el.style.height = 'auto';
		el.style.height = `${el.scrollHeight}px`;
	}

	setValue(text: string) {
		this.value = text;
		this.focus();
		this.autoGrow();
	}

	focus() {
		this.inputEl()?.nativeElement.focus();
	}
}
